import React from "react";
import { Percent, History, Undo2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

/**
 * Price Adjustment Modal
 * Modal for adjusting prices by percentage
 */
export const PriceAdjustModal = ({
  isOpen,
  onClose,
  adjustData,
  setAdjustData,
  onApply,
  isAdjusting,
  quoteTypesCount,
  selectedIdsCount,
  materials = []
}) => {
  const percentage = parseFloat(adjustData.percentage);
  const isIncrease = percentage > 0;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Percent className="w-5 h-5" />
            Ajustar Precios por Porcentaje
          </DialogTitle>
          <DialogDescription>
            Aumenta o disminuye los costos de todos o algunos tipos
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Percentage input */}
          <div className="space-y-2">
            <Label>Porcentaje de ajuste *</Label>
            <div className="flex items-center gap-2">
              <Input
                data-testid="price-adjust-percentage"
                type="number"
                step="0.1"
                placeholder="Ej: 5, -10, 15.5"
                value={adjustData.percentage}
                onChange={(e) => setAdjustData(prev => ({ ...prev, percentage: e.target.value }))}
                className="font-mono"
              />
              <span className="text-lg font-bold text-slate-400">%</span>
            </div>
            <p className="text-xs text-slate-500">
              Positivo para aumentar, negativo para disminuir
            </p>
          </div>

          {/* Apply to selection */}
          <div className="space-y-2">
            <Label>Aplicar a</Label>
            <div className="flex flex-wrap gap-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="applyTo"
                  value="all"
                  checked={adjustData.applyTo === "all"}
                  onChange={() => setAdjustData(prev => ({ ...prev, applyTo: "all" }))}
                  className="w-4 h-4"
                />
                <span className="text-sm">Todos ({quoteTypesCount})</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="applyTo"
                  value="selected"
                  checked={adjustData.applyTo === "selected"}
                  onChange={() => setAdjustData(prev => ({ ...prev, applyTo: "selected" }))}
                  className="w-4 h-4"
                  disabled={selectedIdsCount === 0}
                />
                <span className={`text-sm ${selectedIdsCount === 0 ? 'text-slate-400' : ''}`}>
                  Seleccionados ({selectedIdsCount})
                </span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="applyTo"
                  value="filtered"
                  checked={adjustData.applyTo === "filtered"}
                  onChange={() => setAdjustData(prev => ({ ...prev, applyTo: "filtered" }))}
                  className="w-4 h-4"
                />
                <span className="text-sm">Por filtros</span>
              </label>
            </div>
            {selectedIdsCount === 0 && adjustData.applyTo !== "selected" && (
              <p className="text-xs text-slate-500">
                Selecciona tipos en la tabla para habilitar la opción &quot;Seleccionados&quot;
              </p>
            )}
          </div>

          {/* Filters (shown only when "filtered" is selected) */}
          {adjustData.applyTo === "filtered" && (
            <div className="space-y-3 p-3 bg-slate-50 rounded-lg">
              <p className="text-xs font-medium text-slate-600 uppercase">Filtros (combina como desees)</p>
              
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Material</Label>
                  <Select 
                    value={adjustData.filterMaterial || "all"}
                    onValueChange={(v) => setAdjustData(prev => ({ ...prev, filterMaterial: v === "all" ? "" : v }))}
                  >
                    <SelectTrigger className="h-8 text-sm">
                      <SelectValue placeholder="Todos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      {materials.map(mat => (
                        <SelectItem key={mat} value={mat}>{mat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-1">
                  <Label className="text-xs">Color</Label>
                  <Select 
                    value={adjustData.filterColor || "all"}
                    onValueChange={(v) => setAdjustData(prev => ({ ...prev, filterColor: v === "all" ? "" : v }))}
                  >
                    <SelectTrigger className="h-8 text-sm">
                      <SelectValue placeholder="Todos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      <SelectItem value="Blanco">Blanco</SelectItem>
                      <SelectItem value="Negro">Negro</SelectItem>
                      <SelectItem value="Gris">Gris</SelectItem>
                      <SelectItem value="Natural">Natural</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs">Nombre contiene</Label>
                <Input
                  placeholder="Ej: MAMPARA, VENTANA"
                  value={adjustData.filterName}
                  onChange={(e) => setAdjustData(prev => ({ ...prev, filterName: e.target.value }))}
                  className="h-8 text-sm"
                />
              </div>
            </div>
          )}

          {/* Preview */}
          {adjustData.percentage && !isNaN(percentage) && (
            <div className={`p-3 rounded-lg ${isIncrease ? 'bg-red-50 border border-red-200' : 'bg-emerald-50 border border-emerald-200'}`}>
              <p className={`text-sm font-medium ${isIncrease ? 'text-red-700' : 'text-emerald-700'}`}>
                {isIncrease ? '⬆️ Aumento' : '⬇️ Disminución'} del {Math.abs(percentage)}%
              </p>
              <p className="text-xs text-slate-600 mt-1">
                Ejemplo: $100 → ${(100 * (1 + percentage / 100)).toFixed(2)}
              </p>
            </div>
          )}
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button 
            onClick={onApply}
            disabled={isAdjusting || !adjustData.percentage}
            data-testid="price-adjust-submit-btn"
            className={isIncrease ? 'bg-red-600 hover:bg-red-700' : 'bg-emerald-600 hover:bg-emerald-700'}
          >
            {isAdjusting ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                Aplicando...
              </>
            ) : (
              <>
                <Percent className="w-4 h-4 mr-2" />
                Aplicar Ajuste
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Adjustment History Modal
 * Modal for viewing and undoing price adjustments
 */
export const AdjustmentHistoryModal = ({
  isOpen,
  onClose,
  history,
  isLoading,
  onUndo,
  isUndoing
}) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="w-5 h-5" />
            Historial de Ajustes de Precios
          </DialogTitle>
          <DialogDescription>
            Registro de todos los ajustes aplicados. Puedes revertir los más recientes.
          </DialogDescription>
        </DialogHeader>
        
        <ScrollArea className="h-[400px] pr-4">
          {isLoading ? (
            <div className="flex items-center justify-center h-40">
              <div className="w-8 h-8 border-4 border-slate-200 border-t-slate-600 rounded-full animate-spin" />
            </div>
          ) : history.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-40 text-slate-500">
              <History className="w-10 h-10 mb-3 text-slate-300" />
              <p>No hay ajustes registrados</p>
            </div>
          ) : (
            <div className="space-y-3">
              {history.map((adj) => (
                <div 
                  key={adj.id}
                  className={`p-4 rounded-lg border ${adj.undone ? 'bg-slate-50 border-slate-200 opacity-60' : 'bg-white border-slate-200'}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                          adj.percentage > 0 
                            ? 'bg-red-100 text-red-700' 
                            : 'bg-emerald-100 text-emerald-700'
                        }`}>
                          {adj.percentage > 0 ? '⬆️' : '⬇️'} {adj.action} {Math.abs(adj.percentage)}%
                        </span>
                        {adj.undone && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-200 text-slate-600">
                            Revertido
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-slate-700 font-medium">
                        {adj.types_affected} tipos afectados
                      </p>
                      <p className="text-xs text-slate-500 mt-1">
                        {adj.filter_description}
                      </p>
                      <p className="text-xs text-slate-400 mt-2">
                        {new Date(adj.timestamp).toLocaleString('es-MX', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                    
                    {!adj.undone && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onUndo(adj.id)}
                        disabled={isUndoing === adj.id}
                        className="shrink-0"
                      >
                        {isUndoing === adj.id ? (
                          <div className="w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <>
                            <Undo2 className="w-4 h-4 mr-1" />
                            Revertir
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
